import React from 'react';
import PropTypes from 'prop-types';
import AbstractPicker, { connect } from '../AbstractPicker';
import Slider from '../../Slider/Slider';
import Tooltip from '../../Popup/Tooltip';
import { TOP } from '../../Popup/alignments';
import { replace } from '../../../state/tac/replace';
import emitter from '../../../emitter';
import { CONF_MENU_CHANGE } from '../../../settings/events';
import tacHelpers from '../../../state/tac/tacHelpers';
import productService from '../../../services/products';
import { ceil } from '../../../util/round';
import styles from './ConfExtendableSlider.module.less';
import { range } from '../range';
import { ITEMS } from '../../../constants';
import {
  thunkUpdateItem,
  thunkUpdateMultiple,
} from '../../../state/tac/tacThunks';
import localStatisticsReporter from '../../../services/statistics/insights/custom/local/localStatisticsReporter';

class ConfExtendableSlider extends AbstractPicker {
  static propTypes = {
    limits: PropTypes.object.isRequired,
    item: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    topAncestor: PropTypes.object.isRequired,
    tac: PropTypes.object.isRequired,
    mountOffset: PropTypes.number.isRequired,
    useMetricMeasures: PropTypes.bool.isRequired,
  };

  reportChange(width, itemId) {
    localStatisticsReporter.reportConfMenuSliderChange(width, itemId);
  }

  onNegativeCrDiff = (diff, chain, newWidth, sidewallConnections = {}) => {
    const { topAncestor, dispatch, tac } = this.props;

    const extCrs = tacHelpers
      .getClothesRails({ items: [topAncestor] })
      .filter(item => productService.isExtendable(item));

    const moveLeft = Math.ceil(diff / 2);
    const moveRight = Math.floor(diff / 2);

    const itemsToUpdate = [];
    const newLeftEnd = Object.assign(
      {},
      chain.leftEnd,
      sidewallConnections.left
        ? {}
        : {
            x: chain.leftEnd.x - (sidewallConnections.right ? diff : moveLeft),
          }
    );

    if (newLeftEnd.itemid === topAncestor.itemid) {
      extCrs.forEach(cr => {
        const newCrItem = Object.assign({}, cr, { width: newWidth });
        replace(newLeftEnd.items, newCrItem);
      });
    } else {
      const newTopAncestor = Object.assign({}, topAncestor);

      extCrs.forEach(cr => {
        const newCrItem = Object.assign({}, cr, { width: newWidth });
        replace(newTopAncestor.items, newCrItem);
      });

      itemsToUpdate.push(newTopAncestor);
    }

    const newRightEnd = Object.assign(
      {},
      chain.rightEnd,
      sidewallConnections.right
        ? {}
        : {
            x: chain.rightEnd.x + (sidewallConnections.left ? diff : moveRight),
          }
    );

    itemsToUpdate.push(newLeftEnd, newRightEnd);

    dispatch(
      thunkUpdateMultiple(itemsToUpdate, tac, {
        origin: 'dialog',
        optionValue: newWidth,
        moveOthers: true,
        triggerItem: newLeftEnd,
      })
    );
  };

  onPositiveCrDiff = (diff, chain, newWidth, sidewallConnections = {}) => {
    const { item, topAncestor, dispatch, tac } = this.props;

    const newCrItem = Object.assign({}, item, { width: newWidth });

    const moveLeft = Math.floor(diff / 2);
    const moveRight = Math.ceil(diff / 2);

    let newTopAncestor;

    if (!sidewallConnections.left) {
      const space = tacHelpers.getSpace(tac);

      const rightOverflow =
        chain.rightEnd.x + chain.rightEnd.width + moveRight - space.width;

      const leftOverflow = chain.leftEnd.x - moveLeft - space.x;

      newTopAncestor = Object.assign({}, topAncestor, {
        x: Math.max(
          topAncestor.x -
            moveLeft -
            Math.max(rightOverflow, 0) -
            Math.min(leftOverflow, 0),
          space.x
        ),
      });
    } else {
      newTopAncestor = Object.assign({}, topAncestor);
    }

    replace(newTopAncestor.items, newCrItem);

    dispatch(
      thunkUpdateItem(newTopAncestor, tac, {
        origin: 'dialog',
        optionValue: newWidth,
        moveOthers: true,
        triggerItem: newTopAncestor,
      })
    );
  };

  onRelease = value => {
    const { item, topAncestor, tac, limits, mountOffset } = this.props;

    // Diff needs to be even mm
    let newWidth = parseInt(value);

    // Slider doesn't know that item can actually be a bit wider
    newWidth += mountOffset * 2;

    if (newWidth < limits.min) {
      newWidth = limits.min;
    } else if (newWidth > limits.max) {
      newWidth = limits.max;
    }

    const diff = newWidth - item.width;

    if (diff !== 0) {
      if (productService.isType(item, ITEMS.CLOTHES_RAIL)) {
        const chain = tacHelpers.getClothesRailChain(tac, topAncestor, item);

        const sidewallConnections = {
          left: productService.isType(chain.leftEnd, 'sidewall'),
          right: productService.isType(chain.rightEnd, 'sidewall'),
        };
        if (diff > 0) {
          this.onPositiveCrDiff(diff, chain, newWidth, sidewallConnections);
        } else {
          this.onNegativeCrDiff(diff, chain, newWidth, sidewallConnections);
        }

        this.reportChange(newWidth, item.id);
        emitter.emit(CONF_MENU_CHANGE);
      } else if (productService.isType(item, ITEMS.SECTION)) {
        const updItem = { ...item, width: newWidth, value: newWidth };
        updItem.moveDirection = range.getMoveDirection(
          item,
          updItem,
          'width',
          tac
        );
        this.reportChange(newWidth, item.id);
        this.switch({ from: item, to: updItem, switchingProp: 'width' });
      }
    }
  };

  render() {
    const { item, limits, useMetricMeasures, mountOffset } = this.props;
    const itemWidth = item.width - mountOffset * 2;

    const displayedWidth = useMetricMeasures
      ? ceil(itemWidth, 10)
      : ceil(itemWidth, 1);

    return (
      <Tooltip alignment={TOP} spacing={2} ignorePadding={true}>
        <div className={styles.wrapper}>
          <Slider
            key="width"
            disabled={limits.displayedMin >= limits.displayedMax}
            minVal={limits.displayedMin}
            maxVal={limits.displayedMax}
            initialValue={displayedWidth}
            step={useMetricMeasures ? 10 : 25.4 / 2}
            onRelease={this.onRelease}
          />
        </div>
      </Tooltip>
    );
  }
}

export default connect(ConfExtendableSlider);
